import { EPlatform } from '@enums/platform.enum';
import backendHttpClient from '@services/_httpClient/backend.http-client';

import type { AddInterventionDocumentsPayload } from './dtos/addDocuments.dto';
import type { CreateInterventionPayload } from './dtos/create.dto';
import type { GetAllInterventionsParams } from './dtos/getAll.dto';
import type {
  UpdateInterventionDto,
  UpdateInterventionPayload,
} from './dtos/update.dto';
import type { InterventionRepository } from './repository';
import type {
  AddInterventionDocumentsOutput,
  AddInterventionDocumentsResponse,
} from './types/addDocuments.type';
import type { CreateInterventionResponse } from './types/create.type';
import type {
  GetAllInterventionsOutput,
  GetAllInterventionsResponse,
} from './types/getAll.type';
import type {
  GetAllInterventionDocumentsOutput,
  GetAllInterventionDocumentsParams,
  GetAllInterventionDocumentsQueryParams,
} from './types/getAllDocuments.type';
import type {
  GetAllInterventionNotesOutput,
  GetAllInterventionNotesParams,
  GetAllInterventionNotesQueryParams,
  GetAllInterventionNotesResponse,
} from './types/getAllNotes.type';
import type {
  GetInterventionByIdOutput,
  GetInterventionByIdResponse,
} from './types/getById.type';
import type {
  UpdateInterventionOutput,
  UpdateInterventionResponse,
} from './types/update.type';

class InterventionService implements InterventionRepository {
  public async create(payload: CreateInterventionPayload): Promise<void> {
    await backendHttpClient.post<
      CreateInterventionResponse,
      CreateInterventionPayload
    >({
      path: 'interventions',
      body: {
        serviceRequestId: payload.serviceRequestId,
        serviceId: payload.serviceId,
        remark: payload.remark,
      },
    });
  }

  public async getAll(
    params: GetAllInterventionsParams,
  ): Promise<GetAllInterventionsOutput> {
    const { data } = await backendHttpClient.get<
      GetAllInterventionsResponse,
      GetAllInterventionsParams
    >({
      path: 'interventions',
      params: {
        page: params.page,
        perPage: params.perPage,
        status: params.status,
      },
    });

    return {
      count: data.count,
      list: data.list,
      pagination: data.pagination,
    };
  }

  public async getById(id: string): Promise<GetInterventionByIdOutput> {
    const { data } = await backendHttpClient.get<GetInterventionByIdResponse>({
      path: `interventions/${id}`,
    });

    return { intervention: data.intervention };
  }

  public async update(
    payload: UpdateInterventionPayload,
  ): Promise<UpdateInterventionOutput> {
    const { data } = await backendHttpClient.put<
      UpdateInterventionResponse,
      UpdateInterventionDto
    >({
      path: `interventions/${payload.id}`,
      body: {
        visitDate: payload.visitDate,
        workDate: payload.workDate,
        workEndDate: payload.workEndDate,
        remark: payload.remark,
        status: payload.status,
        invoiceAmount: payload.invoiceAmount,
        invoiceAmountExclVat: payload.invoiceAmountExclVat,
        invoiceVatType: payload.invoiceVatType,
        quoteAmount: payload.quoteAmount,
        quoteAmountExclVat: payload.quoteAmountExclVat,
        quoteVatType: payload.quoteVatType,
        satisfaction: payload.satisfaction,
      },
    });

    return { intervention: data.intervention };
  }

  public async addDocuments(
    payload: AddInterventionDocumentsPayload,
  ): Promise<AddInterventionDocumentsOutput> {
    const formData = new FormData();

    payload.documents.forEach(document => {
      formData.append('file', document.file);
    });

    formData.append(
      'metadata',
      JSON.stringify(
        payload.documents.map(doc => ({
          fileName: doc.file.name,
          type: doc.type,
        })),
      ),
    );

    const { data } = await backendHttpClient.post<
      AddInterventionDocumentsResponse,
      FormData
    >({
      path: `interventions/${payload.id}/documents`,
      body: formData,
    });

    return {
      uploaded: data.list,
    };
  }

  public async getAllDocuments(
    params: GetAllInterventionDocumentsParams,
  ): Promise<GetAllInterventionDocumentsOutput> {
    const { data } = await backendHttpClient.get<
      GetAllInterventionDocumentsOutput,
      GetAllInterventionDocumentsQueryParams
    >({
      path: `interventions/${params.id}/documents`,
      params: {
        types: params.types,
      },
    });

    return {
      list: data.list,
    };
  }
  public async getAllNotes(
    params: GetAllInterventionNotesParams,
  ): Promise<GetAllInterventionNotesOutput> {
    const { data } = await backendHttpClient.get<
      GetAllInterventionNotesResponse,
      GetAllInterventionNotesQueryParams
    >({
      path: `interventions/${params.id}/notes`,
      params: {
        page: params.page,
        perPage: params.perPage,
        platform: EPlatform.Connect,
      },
    });

    return {
      list: data.list,
      pagination: data.pagination,
    };
  }
}

export const interventionService = new InterventionService();
